const DATE_RE =
  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;
const NUMBER_RE = /^-?\d+(\.\d+)?$/;
const BOOL_VALUES = new Set(["true", "false", "yes", "no", "0", "1"]);

const matchRatio = (samples, predicate) => {
  if (samples.length === 0) return 0;
  const hits = samples.filter(predicate).length;
  return hits / samples.length;
};

const confidenceFor = (samples, ratio) => {
  if (samples.length <= 1) return "LOW";
  if (ratio >= 1) return "HIGH";
  if (ratio >= 0.8) return "MEDIUM";
  return "LOW";
};

export const inferTypeFromSamples = samples => {
  const cleaned = (samples ?? [])
    .map(s => (s == null ? "" : String(s).trim()))
    .filter(Boolean);

  if (cleaned.length === 0) {
    return { type: "text", confidence: "LOW" };
  }

  const dateRatio = matchRatio(cleaned, s => DATE_RE.test(s));
  if (dateRatio >= 0.8) {
    return { type: "date", confidence: confidenceFor(cleaned, dateRatio) };
  }

  const numberRatio = matchRatio(cleaned, s => NUMBER_RE.test(s));
  if (numberRatio >= 0.8) {
    return { type: "number", confidence: confidenceFor(cleaned, numberRatio) };
  }

  const boolRatio = matchRatio(cleaned, s => BOOL_VALUES.has(s.toLowerCase()));
  if (boolRatio >= 0.8) {
    return { type: "boolean", confidence: confidenceFor(cleaned, boolRatio) };
  }

  return { type: "text", confidence: confidenceFor(cleaned, 1) };
};

// Single/multi-select require ``options.choices`` server-side. When the
// inference returns choices we default to them so the user can submit the
// inferred schema without hand-entering each value; switching the type away
// from select drops the options.
//
// Multi-select cells are comma-split at import time, so multi_select seeds
// from ``multi_select_choices`` (atomic values) — not ``choices`` (raw
// whole-cell strings), which would produce composite options like
// ``"el1,el2"`` that no row would ever match. A present-but-null value
// means the atomic set exceeded the cap, so we must NOT fall back to the
// composite ``choices``; only an absent field (legacy payload predating
// ``multi_select_choices``) falls back.
export const defaultOptionsForInference = inf => {
  if (inf?.type !== "single_select" && inf?.type !== "multi_select") {
    return null;
  }
  const source =
    inf?.type === "multi_select" && inf?.multi_select_choices !== undefined
      ? inf.multi_select_choices
      : inf?.choices;
  const choices = Array.isArray(source) ? source : null;
  if (!choices || choices.length === 0) return null;
  return { choices };
};

export const applyMappingToSchema = (parseResult, edits = {}) => {
  const inferences =
    parseResult?.suggested_schema ?? parseResult?.column_inferences ?? [];
  return inferences.map((inf, index) => {
    const edit = edits[inf.csv_column_name] ?? {};
    const effectiveType = edit.type ?? inf.type;
    return {
      csv_column_name: inf.csv_column_name,
      csv_column_index: inf.csv_column_index ?? index,
      include: edit.include ?? true,
      display_name: edit.display_name ?? inf.suggested_display_name,
      field_id: edit.field_id ?? inf.suggested_field_id,
      type: effectiveType,
      is_required: edit.is_required ?? false,
      is_unique: edit.is_unique ?? false,
      options:
        edit.options ??
        defaultOptionsForInference({ ...inf, type: effectiveType }),
      samples: inf.samples,
      confidence: inf.confidence,
      distinct_count: inf.distinct_count,
      null_count: inf.null_count,
      choices: inf.choices ?? null,
      multi_select_choices: inf.multi_select_choices ?? null,
      type_match_rates: inf.type_match_rates ?? null,
      sampled_count: inf.sampled_count ?? 0,
    };
  });
};

// Build mapping rows for append mode. Each CSV column is auto-matched to
// an existing schema column when the field_id matches (case-insensitive);
// otherwise the row is left unmatched (include=false) and the UI shows
// "Skip this column" as the only option.
export const applyMappingForAppend = (
  parseResult,
  existingSchema,
  edits = {}
) => {
  const inferences =
    parseResult?.suggested_schema ?? parseResult?.column_inferences ?? [];
  const existingColumns = existingSchema?.columns ?? [];
  const byFieldId = new Map(existingColumns.map(c => [c.field_id, c]));

  return inferences.map((inf, index) => {
    const edit = edits[inf.csv_column_name] ?? {};
    // Auto-match by suggested_field_id, then by display_name slug fallback.
    const autoMatch =
      byFieldId.get(inf.suggested_field_id) ??
      existingColumns.find(
        c =>
          (c.display_name ?? "").toLowerCase() ===
          (inf.csv_column_name ?? "").toLowerCase()
      ) ??
      null;

    const fieldId = edit.field_id ?? autoMatch?.field_id ?? null;
    const matched = fieldId ? (byFieldId.get(fieldId) ?? null) : null;

    const matchedType = matched?.type ?? inf.type;
    const isSelectType =
      matchedType === "single_select" || matchedType === "multi_select";
    return {
      csv_column_name: inf.csv_column_name,
      csv_column_index: inf.csv_column_index ?? index,
      include: edit.include ?? Boolean(matched),
      display_name: matched?.display_name ?? inf.suggested_display_name,
      field_id: fieldId,
      type: matchedType,
      is_required: matched?.is_required ?? false,
      is_unique: matched?.is_unique ?? false,
      options: matched?.options ?? null,
      // Append-mode opt-in: existing columns stay strict by default. The
      // user ticks the checkbox in Step3Mapping when they want this import
      // to extend a select column's choices instead of skipping novel
      // values. Only meaningful for single/multi_select.
      allow_choice_additions: isSelectType
        ? (edit.allow_choice_additions ?? false)
        : false,
      samples: inf.samples,
      confidence: inf.confidence,
      distinct_count: inf.distinct_count,
      null_count: inf.null_count,
      type_match_rates: inf.type_match_rates ?? null,
      sampled_count: inf.sampled_count ?? 0,
      // Append-only metadata so MappingRow can render locked controls.
      append_locked: true,
      append_matched: Boolean(matched),
    };
  });
};

// Validate an append-mode mapping. Every included row must map to a
// field_id that exists in the supplied schema; unmatched rows must be
// excluded.
export const validateAppendMapping = (rows, existingSchema) => {
  const errors = [];
  const knownFieldIds = new Set(
    (existingSchema?.columns ?? []).map(c => c.field_id)
  );
  (rows ?? []).forEach((row, index) => {
    if (!row.include) return;
    if (!row.field_id || !knownFieldIds.has(row.field_id)) {
      errors.push({
        row_index: index,
        csv_column_name: row.csv_column_name,
        field: "field_id",
        message: "Choose an existing column or skip this CSV column",
      });
    }
  });
  return errors;
};

const FIELD_ID_PATTERN = /^[a-z][a-z0-9_]{0,62}$/;
const FIELD_ID_FORMAT_MESSAGE =
  "Field ID must start with a lowercase letter and use only lowercase letters, numbers, and underscores";

export const validateMapping = rows => {
  const errors = [];
  const allRows = rows ?? [];

  const displayCounts = new Map();
  const fieldIdCounts = new Map();
  for (const row of allRows) {
    if (!row.include) continue;
    const dn = (row.display_name ?? "").trim();
    if (dn) {
      displayCounts.set(dn, (displayCounts.get(dn) ?? 0) + 1);
    }
    const fid = (row.field_id ?? "").trim();
    if (fid) {
      fieldIdCounts.set(fid, (fieldIdCounts.get(fid) ?? 0) + 1);
    }
  }

  allRows.forEach((row, index) => {
    if (!row.include) return;
    const dn = (row.display_name ?? "").trim();
    const fid = (row.field_id ?? "").trim();
    if (!dn) {
      errors.push({
        row_index: index,
        csv_column_name: row.csv_column_name,
        field: "display_name",
        message: "Display name is required",
      });
    } else if (displayCounts.get(dn) > 1) {
      errors.push({
        row_index: index,
        csv_column_name: row.csv_column_name,
        field: "display_name",
        message: `Duplicate display name "${dn}"`,
      });
    }
    if (!fid) {
      errors.push({
        row_index: index,
        csv_column_name: row.csv_column_name,
        field: "field_id",
        message: "Field ID is required",
      });
    } else if (!FIELD_ID_PATTERN.test(fid)) {
      errors.push({
        row_index: index,
        csv_column_name: row.csv_column_name,
        field: "field_id",
        message: FIELD_ID_FORMAT_MESSAGE,
      });
    } else if (fieldIdCounts.get(fid) > 1) {
      errors.push({
        row_index: index,
        csv_column_name: row.csv_column_name,
        field: "field_id",
        message: `Duplicate field id "${fid}"`,
      });
    }
    if (row.type === "relationship" && !row.options?.target_type) {
      errors.push({
        row_index: index,
        csv_column_name: row.csv_column_name,
        field: "options",
        message: "Pick a record type to link to",
      });
    }
    if (row.type === "single_select" || row.type === "multi_select") {
      const choices = Array.isArray(row.options?.choices)
        ? row.options.choices
        : null;
      if (!choices || choices.length === 0) {
        errors.push({
          row_index: index,
          csv_column_name: row.csv_column_name,
          field: "options",
          message: "At least one choice is required",
        });
      }
    }
  });

  return errors;
};

export const formatBytes = bytes => {
  if (!bytes || bytes === 0) return "0 B";
  const KB = 1024;
  const MB = KB * 1024;
  if (bytes >= MB) {
    return `${(bytes / MB).toFixed(bytes % MB === 0 ? 0 : 1)} MB`;
  }
  if (bytes >= KB) {
    return `${(bytes / KB).toFixed(bytes % KB === 0 ? 0 : 1)} KB`;
  }
  return `${bytes} B`;
};

export const summarizeProgress = info => {
  if (!info || !info.custom_table_import) return null;
  return info.custom_table_import;
};

export const deriveFieldId = displayName => {
  if (!displayName) return "";
  return String(displayName)
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[0-9]+/, "")
    .replace(/^_+/, "");
};
